import { Autocomplete, Chip, createFilterOptions, TextField } from '@mui/material';
import { useMemo, useRef } from 'react';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

type Props = {
  name: string;
  label: string;
  labelProp?: string; // Label prop for the option
  valueProp?: string; // Value prop for the option
  options?: any[]; // Predefined options
  hideOptions?: boolean; // 🔥 Prop to manually hide options
  multiple?: boolean; // Support single & multi-select
  rules?: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  required?: boolean;
  disabled?: boolean;

  allowClear?: boolean;
  onChange?: (value: any) => void; // if provided you must update the attribute yourself, as this will bypass internal react hook form updating
};

const VNAutocomplete = ({
  name,
  label,
  labelProp,
  valueProp,
  options = [],
  hideOptions = false,
  multiple = false,
  rules,
  required = false,
  disabled = false,
  allowClear = false,
  onChange = null,
}: Props) => {
  const { control, getValues } = useFormContext();

  const selectedValues: string[] | string = getValues(name) || multiple ? [] : '';
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionValues = useMemo(() => {
    const predefinedValues =
      typeof options?.[0] === 'object' ? options.map((option) => option[valueProp] ?? option.value) : options;

    // Ensure user-added values are included
    return Array.from(new Set([...predefinedValues, ...(Array.isArray(selectedValues) ? selectedValues : [selectedValues])]));
  }, [options, selectedValues]);

  const optionLabels = useMemo(() => {
    const predefinedLabels =
      typeof options?.[0] === 'object'
        ? Object.fromEntries(options.map((option) => [option[valueProp] ?? option.value, option[labelProp] ?? option.label]))
        : Object.fromEntries(options.map((option) => [option, option]));

    // Include user-added values with the same label as value
    if (Array.isArray(selectedValues)) {
      selectedValues.forEach((val) => {
        if (!predefinedLabels[val]) {
          predefinedLabels[val] = val;
        }
      });
    }

    return predefinedLabels;
  }, [options, selectedValues]);

  // Custom filter function that filters by the label text
  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => optionLabels[option] || '',
  });

  const resetInput = () => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.blur();
      }
    }, 100);
  };

  return (
    <div className="vn-autocomplete">
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? `${label} is required` : undefined,
          ...rules,
        }}
        render={({ field: { value, onChange: innerOnChange }, fieldState }) => (
          <Autocomplete
            disableCloseOnSelect
            multiple={multiple}
            freeSolo={hideOptions}
            disableClearable={!allowClear}
            options={hideOptions ? [] : optionValues} // Hide options manually
            disabled={disabled}
            value={(() => {
              if (multiple) {
                return Array.isArray(value) ? value : [];
              }
              return value || '';
            })()}
            onChange={(_, newValue) => {
              if (onChange) {
                onChange(newValue);
                return;
              }
              if (!multiple) {
                innerOnChange(newValue);
              } else {
                const cleanedValue = Array.from(
                  new Set(
                    newValue
                      .map((v: string) => v.trim())
                      // remove empty
                      .filter(Boolean)
                  )
                );
                innerOnChange(cleanedValue);
              }
              // resetInput();
            }}
            filterOptions={hideOptions ? () => [] : filterOptions}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                {optionLabels[option] ?? ''}
              </li>
            )}
            renderTags={(tagValue, getTagProps) => {
              return tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  classes={{ root: 'vn-chip' }}
                  size="small"
                  key={option}
                  label={optionLabels[option] ?? ''}
                />
              ));
            }}
            getOptionLabel={(option) => optionLabels[option] ?? ''}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${label}${required ? ' *' : ''}`}
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                inputRef={inputRef}
              />
            )}
          />
        )}
      />
    </div>
  );
};

export default VNAutocomplete;
